import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { getTrialLengthDays } from "@intelligems/ig-utils/stripe";
import {
  getBillingGatePresentation,
  MODULE_STATIC_CONFIG,
} from "@src/components/BillingSettings2026/workflow";
import { TRIAL_WELCOME_MODULES } from "@src/features/sharedExperience/components/alertsAndWarnings/moduleTrialWelcomeModules";
import { MODULE_TRIAL_DESCRIPTION } from "@src/features/sharedExperience/components/moduleEmptyStateCopy";
import {
  useModuleTrialWelcome,
  welcomeAlertKey,
} from "@src/features/sharedExperience/hooks/use-module-trial-welcome";
import { useDialogs } from "@src/hooks/dialog";
import { useDismissAlert } from "@src/hooks/useDismissAlert";
import { SETTINGS_ROUTES } from "@src/routes/routes";
import { useDispatch } from "@src/store";
import { setConfetti } from "@src/store/slices/global";

import { ModuleTrialWelcomeDialogView } from "./ModuleTrialWelcomeDialog.view";

export function ModuleTrialWelcomeDialog() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const dismissAlert = useDismissAlert();
  const { openDialog } = useDialogs();
  const { welcomeTrials, newestTrial, isPending } = useModuleTrialWelcome();

  const isOnSettings = pathname.startsWith(SETTINGS_ROUTES.Settings);
  const isOpen = isPending && !isOnSettings;

  useEffect(() => {
    if (isOpen) {
      dispatch(setConfetti(true));
    }
  }, [isOpen, dispatch]);

  if (!newestTrial) {
    return null;
  }

  const welcomeModule = TRIAL_WELCOME_MODULES[newestTrial.moduleId];
  const { Graphic } = welcomeModule;
  const { videoPlaybackId } = getBillingGatePresentation(newestTrial.moduleId);

  const trialDays = getTrialLengthDays(
    newestTrial.startDate,
    newestTrial.endDate
  );
  const moduleDisplayName =
    MODULE_STATIC_CONFIG[newestTrial.moduleId]?.name ?? newestTrial.moduleId;
  const isBfcmTrial = newestTrial.trigger === "bfcm_2026";
  const chipLabel = isBfcmTrial
    ? "Free Trial Unlocked"
    : `${trialDays} days free`;
  const title = isBfcmTrial
    ? `You've unlocked ${moduleDisplayName} until the new year!`
    : `You've unlocked ${trialDays} days of ${moduleDisplayName}!`;

  const onClose = () => {
    for (const trial of welcomeTrials) {
      dismissAlert(welcomeAlertKey(trial));
    }
  };

  const onViewTemplates = () => {
    void navigate(welcomeModule.route);
    openDialog("createExperiment");
  };

  return (
    <ModuleTrialWelcomeDialogView
      isOpen={isOpen}
      title={title}
      chipLabel={chipLabel}
      moduleDisplayName={moduleDisplayName}
      description={MODULE_TRIAL_DESCRIPTION[newestTrial.moduleId]}
      videoPlaybackId={videoPlaybackId}
      graphic={<Graphic sx={{ width: "100%", height: 200 }} />}
      onClose={onClose}
      onViewTemplates={onViewTemplates}
    />
  );
}
